import { ReactNode, type FC } from 'react';
import { useTranslation } from '../../../../providers/UIProvider';
import type { FormatterProps } from '../types/editor-config';
import { FormatterError } from './FormatterError';

export interface CodelistFormatterOptions {
  /** Translation namespace used to resolve each code (e.g. `components.paymentMethod`). */
  namespace: string;
  /** Codelist whose values define the known set; unknown codes render as-is. */
  knownValues: Record<string, string>;
}

/**
 * Builds a `FieldEditor` formatter for an enum-like codelist. Each non-empty
 * value is translated via the provided namespace; empty values render as `—`;
 * values absent from `knownValues` render as the raw code (avoids leaking
 * missing translation keys when upstream sends a new code).
 */
export function createCodelistFormatter({
  namespace,
  knownValues
}: CodelistFormatterOptions): FC<FormatterProps> {
  const knownSet = new Set<string>(Object.values(knownValues));

  function CodelistFormatter({ value, errors }: FormatterProps): ReactNode {
    const t = useTranslation(namespace);
    const code = value == null || value === '' ? '' : String(value);

    let text: string;
    if (!code) text = '—';
    else if (!knownSet.has(code)) text = code;
    else text = t(code);

    return (
      <span className='inline-flex flex-col gap-2xs items-start'>
        <span>{text}</span>
        <FormatterError issues={errors} />
      </span>
    );
  }
  CodelistFormatter.displayName = `CodelistFormatter(${namespace})`;
  return CodelistFormatter;
}
